// Real download speed test against this site's own origin.
// LibreSpeed-style: several parallel XHR downloads of an incompressible blob,
// bytes counted via onprogress, aborted when the time budget runs out.
// (XHR instead of fetch on purpose — progress events without streaming readers,
// and API traffic conventions do not apply to a throughput probe.)

export interface SpeedTestOptions {
    url?: string
    durationMs?: number
    streams?: number
    onProgress?: (mbps: number) => void
}

export const measureDownloadMbps = (opts: SpeedTestOptions = {}): Promise<number> => {
    const url = opts.url ?? '/speedtest/blob.bin'
    const durationMs = opts.durationMs ?? 7000
    const streams = opts.streams ?? 3

    return new Promise((resolve, reject) => {
        const start = performance.now()
        let bytes = 0
        let failures = 0
        let finished = false
        const xhrs = new Set<XMLHttpRequest>()

        const currentMbps = () => {
            const dt = (performance.now() - start) / 1000
            return dt > 0 ? (bytes * 8) / dt / 1e6 : 0
        }

        const report = setInterval(() => {
            if (opts.onProgress && (performance.now() - start) > 400) {
                opts.onProgress(Math.round(currentMbps()))
            }
        }, 200)

        const finish = () => {
            if (finished) return
            finished = true
            clearInterval(report)
            clearTimeout(stop)
            xhrs.forEach((x) => x.abort())
            if (bytes === 0) reject(new Error('speed test failed'))
            else resolve(Math.max(1, Math.round(currentMbps())))
        }

        const stop = setTimeout(finish, durationMs)

        const launch = () => {
            if (finished || performance.now() - start >= durationMs) return
            const xhr = new XMLHttpRequest()
            xhrs.add(xhr)
            let loaded = 0
            xhr.open('GET', url + '?r=' + Math.random().toString(36).slice(2), true)
            xhr.responseType = 'arraybuffer'
            xhr.onprogress = (e) => {
                if (xhr.status === 200) {
                    bytes += e.loaded - loaded
                    loaded = e.loaded
                }
            }
            xhr.onload = () => {
                xhrs.delete(xhr)
                if (xhr.status === 200) {
                    launch()
                } else if (++failures >= streams) {
                    finish()
                }
            }
            xhr.onerror = () => {
                xhrs.delete(xhr)
                if (++failures >= streams) finish()
            }
            xhr.send()
        }

        for (let i = 0; i < streams; i++) launch()
    })
}
